// React components
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

// MUI and Material Dashboard components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

// Location Fields component
import LocationFields from "./location-fields";

export default function ItemFields({
    inventoryItem,
    changeHandler,
    batchChangeHandler,
    submitHandler,
    editMode,
    error
}) {
    const navigate = useNavigate();
    const commonProps = { // Props common for the input fields
        display: "flex",
        flexDirection: "column",
        fullWidth: true,
        marginBottom: "1rem",
        marginTop: "0.3rem",
    };
    const [refurbished, setRefurbished] = useState(false);
    const [batchPriceMode, setBatchPriceMode] = useState(false);
    const [currency, setCurrency] = useState("CAD");
    const [qtyPerBox, setQtyPerBox] = useState(0);
    const [symbol, setSymbol] = useState("C$");

    useEffect(() => {
        if (editMode) {
            setRefurbished(Boolean(inventoryItem.refurbished));
        }
    }, [inventoryItem.refurbished]);

    const handleRefurbishedChange = (e) => {
        setRefurbished(e.target.checked);
        changeHandler("refurbished", e.target.checked);
    };

    const handleBatchPriceModeChange = (e) => {
        setBatchPriceMode(e.target.checked);
        changeHandler("batchPriceMode", e.target.checked);
    };

    const handleCurrencyChange = (e) => {
        setCurrency(e.target.value);
        changeHandler("currency", e.target.value);
        let sym = "C$";
        switch (e.target.value) {
            case "USD":
                sym = "$";
                break;
            case "EUR":
                sym = "€";
                break;
            case "GBP":
                sym = "£";
                break;
            case "CAD":
                sym = "C$";
                break;
            default:
                sym = "C$";
        }
        setSymbol(sym);
    };

    // Handles the change in Qty Per Box select field
    const handleQtyPerBoxChange = (e) => {
        setQtyPerBox(e.target.value);
        changeHandler("qtyPerBox", e.target.value);
    };

    return (
        <MDBox mt={2}>
            <Card>
                <MDBox margin={2} p={3}>
                    <MDTypography variant="h4" fontWeight="bold">
                        Inventory Item Details
                    </MDTypography>

                    <LocationFields
                        inventoryItem={inventoryItem}
                        changeHandler={changeHandler}
                        batchChangeHandler={batchChangeHandler}
                        commonProps={commonProps}
                        editMode={editMode}
                    />

                    <Grid container spacing={1}>

                        {/* Qty In */}
                        <Grid item xs={batchPriceMode ? 10 : 12} lg={batchPriceMode ? 10 : 12}>
                            <MDBox {...commonProps}>
                                <FormControl fullWidth>
                                    <MDInput
                                        label={ batchPriceMode ? "Quantity (Number of Boxes)" : "Quantity (Number of Units)"}
                                        id="outlined-adornment-quantity"
                                        fullWidth
                                        value={inventoryItem.quantityIn}
                                        onChange={(e) => changeHandler("quantityIn", e.target.value)}
                                        error={error && error.quantityIn}
                                    />
                                    {error && error.quantityIn && (
                                        <MDTypography variant="caption" color="error">
                                            {"The Quantity field is required."}
                                        </MDTypography>
                                    )}
                                </FormControl>
                            </MDBox>
                        </Grid>

                        {/* Qty Per Box Select */}
                        {batchPriceMode && (
                            <Grid item xs={batchPriceMode ? 2 : 0}>
                                <MDBox {...commonProps}>
                                    <FormControl fullWidth>
                                        <MDInput
                                            id="selector-qty-per-box"
                                            variant="outlined"
                                            value={qtyPerBox}
                                            onChange={handleQtyPerBoxChange}
                                            label="Qty/Box"
                                            type="number"
                                            sx={{ height: '44px' }}
                                        />
                                    </FormControl>
                                </MDBox>
                            </Grid>)}

                    </Grid>

                    {/* Price Per Unit */}
                    <MDBox {...commonProps}>
                        <Grid container spacing={1}>

                            {/* Price Input */}
                            <Grid item xs={10}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="outlined-adornment-amount">{
                                        batchPriceMode ?
                                            "Price Per Box" : "Price Per Unit"
                                    }</InputLabel>
                                    <OutlinedInput
                                        label={batchPriceMode ? "Price Per Box" : "Price Per Unit"}
                                        id="outlined-adornment-amount"
                                        type="number"
                                        startAdornment={<InputAdornment position="start">{symbol}</InputAdornment>}
                                        fullWidth
                                        value={inventoryItem.pricePerUnit}
                                        onChange={(e) => changeHandler("pricePerUnit", e.target.value)}
                                        error={error && error.pricePerUnit}
                                    />
                                    {error && error.pricePerUnit && (
                                        <MDTypography variant="caption" color="error">
                                            {"The Price Per Unit field is required."}
                                        </MDTypography>
                                    )}
                                </FormControl>
                            </Grid>

                            {/* Currency Select */}
                            <Grid item xs={2}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel id="demo-simple-select-standard-label">Currency</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-standard-label"
                                        id="demo-simple-select-standard"
                                        variant="outlined"
                                        value={currency}
                                        onChange={handleCurrencyChange}
                                        label="Currency"
                                        sx={{ height: '44px' }}
                                    >
                                        <MenuItem value="CAD">CAD</MenuItem>
                                        <MenuItem value="USD">USD</MenuItem>
                                        <MenuItem value="GBP">GBP</MenuItem>
                                        <MenuItem value="EUR">EUR</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                        </Grid>
                    </MDBox>

                    {/* Check Boxes */}
                    <MDBox display="flex" alignItems="center">
                        {/* Refurbished */}
                        <MDBox display="flex" alignItems="center">
                            <Checkbox
                                checked={refurbished}
                                onChange={handleRefurbishedChange}
                                color="primary"
                            />
                            <MDTypography variant="h6" fontWeight="light">Refurbished</MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" ml={4}>
                            <Checkbox
                                checked={batchPriceMode}
                                onChange={handleBatchPriceModeChange}
                                color="primary"
                            />
                            <MDTypography variant="h6" fontWeight="light">Price per box</MDTypography>
                        </MDBox>
                    </MDBox>

                    {/* Submit & Cancel buttons */}
                    <MDBox ml="auto" mt={4} mb={2} display="flex" justifyContent="flex-end">
                        <MDBox mx={2}>
                            <MDButton
                                variant="gradient"
                                color="dark"
                                size="small"
                                px={2}
                                mx={2}
                                onClick={() =>
                                    navigate("/inventory-management/", {
                                        state: { value: false, text: "" },
                                    })
                                }
                            >
                                Cancel
                            </MDButton>
                        </MDBox>
                        <MDBox>
                            <MDButton
                                variant="gradient"
                                color="dark"
                                size="small"
                                type="submit"
                                onClick={submitHandler}>
                                {editMode ? "Save" : "Create"}
                            </MDButton>
                        </MDBox>
                    </MDBox>

                </MDBox>
            </Card>
        </MDBox>
    )
}